import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

const POLL_INTERVAL_MS = 2500;
const POLL_LIMIT = 24; // ~60s total

export default function SubscriptionSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "pending", data: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "missing", data: null, error: "No session_id in URL." });
      return undefined;
    }
    let cancelled = false;
    let attempts = 0;
    let timer = null;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await sharedApi.get(`/classifieds/subscriptions/status/${sessionId}`);
        if (cancelled) return;
        if (data?.payment_status === "paid") {
          setStatus({ state: "paid", data, error: null });
          return;
        }
        if (attempts >= POLL_LIMIT) {
          setStatus({ state: "timeout", data, error: "Payment is still processing — please refresh in a minute." });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setStatus({ state: "error", data: null, error: err?.response?.data?.detail || "Couldn't verify payment." });
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-4 md:px-8 py-16 text-center">
      <Seo title="Subscription confirmed" />
      {status.state === "pending" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto text-[color:var(--brand-blue)] animate-spin" />
          <h1 className="mt-4 font-display text-3xl font-bold text-slate-900">Confirming your payment…</h1>
          <p className="mt-2 text-slate-500">Please don't close this tab. It usually takes a few seconds.</p>
        </>
      )}
      {status.state === "paid" && (
        <>
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
          <h1 className="mt-4 font-display text-3xl font-bold text-slate-900">You're all set — kia ora!</h1>
          <p className="mt-2 text-slate-500">Your plan is active. Post ads, boost listings and reach more Kiwis.</p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to="/post-ad" className="rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 transition-colors">
              Post an ad
            </Link>
            <Link to="/my-ads" className="rounded-full border border-soft bg-white hover:border-[color:var(--brand-blue)] text-slate-700 font-semibold px-5 py-2.5 transition-colors">
              My ads
            </Link>
          </div>
        </>
      )}
      {(status.state === "error" || status.state === "missing") && (
        <>
          <XCircle className="w-12 h-12 mx-auto text-red-500" />
          <h1 className="mt-4 font-display text-3xl font-bold text-slate-900">Payment couldn't be verified</h1>
          <p className="mt-2 text-slate-500">{status.error}</p>
          <Link to="/plan-and-pricing" className="mt-6 inline-block rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-5 py-2.5">Back to plans</Link>
        </>
      )}
      {status.state === "timeout" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto text-slate-400" />
          <h1 className="mt-4 font-display text-3xl font-bold text-slate-900">Still processing…</h1>
          <p className="mt-2 text-slate-500">{status.error}</p>
          <button onClick={() => window.location.reload()} className="mt-6 rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-5 py-2.5">
            Refresh
          </button>
        </>
      )}
    </div>
  );
}
